import { RequiredProps, TPAComponentProps } from '../../types';

export interface BoxSelectionProps extends TPAComponentProps, RequiredProps {
  /**
   * Is used to group options together
   */
  name: string;
  /**
   * size variations
   */
  size?: BoxSelectionBoxSize;
  /**
   * Used for SR labelling the group of options
   */
  'aria-label'?: string;
  /**
   * Used for SR labelling the group of options (by id)
   */
  'aria-labelledby'?: string;
  /**
   * The onChange callback
   */
  onChange?({ id }: { id: string }): void;
  /**
   * The Box selected children
   */
  children?: React.ReactNode;
  /**
   * Layout boxes vertically
   */
  vertical?: boolean;
  /**
   * Display inline
   */
  inline?: boolean;
  error?: boolean;
  /** the error message to display */
  errorMessage?: string;
  /**
   * Label that appears on top of the input.
   */
  label?: string;
  /**
   * Pass a component you want to show as the inline suffix
   */
  suffix?: React.ReactNode;
}

export interface BoxSelectionDefaultProps {
  size: BoxSelectionBoxSize;
}

export enum BoxSelectionBoxSize {
  small = 'small',
  xLarge = 'large',
  xSmall = 'xsmall',
}

export enum BoxSelectionDataHooks {
  BOX_SELECTION_WRAPPER = 'box-selection-wrapper',
  BOX_SELECTION_OPTION = 'box-selection-option',
  BOX_SELECTION_OPTION_WRAPPER = 'box-selection-option-wrapper',
  BOX_SELECTION_SUFFIX = 'box-selection-suffix',
}

export enum BoxSelectionDataAttributes {
  DISABLED = 'data-disabled',
  CHECKED = 'data-checked',
  UNAVAILABLE = 'data-unavailable',
  FOCUSED = 'data-focused',
  NATIVE_SELECTOR = 'data-native-selector',
}

export enum BoxSelectionSelectionType {
  RADIO_BUTTON = 'radioButton',
  CHECKBOX = 'checkBox',
}

export interface BoxSelectionOptionProps extends TPAComponentProps {
  /**
   * In order to identify the option. Used in the onChange callback.
   */
  id: string;
  /**
   * Group the different options, received from parent.
   */
  name?: string;
  /**
   * Vertical alignment, received from parent.
   */
  vertical?: boolean;
  /**
   * indicates that the option is selected
   */
  checked?: boolean;
  /**
   * indicates that the option is disabled
   */
  disabled?: boolean;
  /**
   * indicates that the option is unavailable
   */
  unavailable?: boolean;
  /**
   * size variations
   */
  size?: BoxSelectionBoxSize;
  /**
   * The content of the option
   */
  children?: React.ReactNode;
  /**
   * The onChange callback
   */
  onChange?({ id }: { id: string }): void;
  /**
   * Used for SR labelling aria-describedBy
   */
  description?: string;
  /**
   * Choose input type
   */
  type?: BoxSelectionSelectionType;
  /**
   * @deprecated
   * Will be removed in future version.
   * Do not use unless something is broken and you need a fix before a new version is released.   *
   */
  unstableHasInnerWrapper?: boolean;
  /** Will set inner input fields as required - accessibility */
  required?: boolean;
}

export interface BoxSelectionOptionState {
  focused: boolean;
}
